import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Collapse, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as ResetIcon
} from '@mui/icons-material';

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const clampScale = (v) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, v));

/** Image viewer with pinch / wheel zoom, drag to pan and +/-/reset buttons. */
const ZoomableImage = ({ src, alt, height = 360 }) => {
  const boxRef = useRef(null);
  const pointers = useRef(new Map());
  const pinch = useRef(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });

  const reset = useCallback(() => setView({ scale: 1, x: 0, y: 0 }), []);
  useEffect(() => { reset(); }, [src, reset]);

  // Zoom around a point (in container coordinates) keeping that point fixed on screen.
  const zoomAt = useCallback((factor, px, py) => {
    setView((v) => {
      const next = clampScale(v.scale * factor);
      if (next === v.scale) return v;
      const ratio = next / v.scale;
      if (next === MIN_SCALE) return { scale: 1, x: 0, y: 0 };
      return { scale: next, x: px - (px - v.x) * ratio, y: py - (py - v.y) * ratio };
    });
  }, []);

  const center = () => {
    const r = boxRef.current?.getBoundingClientRect();
    return r ? [r.width / 2, r.height / 2] : [0, 0];
  };

  // React registers wheel listeners as passive, so attach natively to be able to preventDefault.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) };
    }
  };

  const onPointerMove = (e) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const cur = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, cur);
    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = Array.from(pointers.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const r = boxRef.current.getBoundingClientRect();
      zoomAt(dist / pinch.current.dist, (a.x + b.x) / 2 - r.left, (a.y + b.y) / 2 - r.top);
      pinch.current.dist = dist;
    } else if (pointers.current.size === 1) {
      setView((v) => (v.scale === 1 ? v : { ...v, x: v.x + cur.x - prev.x, y: v.y + cur.y - prev.y }));
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };

  const zoomed = view.scale > 1;
  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={boxRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          if (zoomed) reset(); else zoomAt(2.5, e.clientX - r.left, e.clientY - r.top);
        }}
        sx={{
          height, overflow: 'hidden', borderRadius: 1, touchAction: 'none', userSelect: 'none',
          cursor: zoomed ? 'grab' : 'zoom-in', '&:active': { cursor: zoomed ? 'grabbing' : 'zoom-in' }
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          draggable={false}
          sx={{
            display: 'block', width: '100%', height: '100%', objectFit: 'contain',
            transformOrigin: '0 0',
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            transition: pointers.current.size ? 'none' : 'transform 0.08s'
          }}
        />
      </Box>
      <Box sx={{
        position: 'absolute', top: 6, right: 6, display: 'flex', alignItems: 'center', gap: 0.25,
        bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1, px: 0.25
      }}>
        <Tooltip title="Zoom out"><span>
          <IconButton size="small" disabled={!zoomed} onClick={() => zoomAt(1 / 1.3, ...center())}><ZoomOutIcon fontSize="small" /></IconButton>
        </span></Tooltip>
        <Typography variant="caption" sx={{ minWidth: 38, textAlign: 'center', fontSize: '0.7rem' }}>
          {Math.round(view.scale * 100)}%
        </Typography>
        <Tooltip title="Zoom in"><span>
          <IconButton size="small" disabled={view.scale >= MAX_SCALE} onClick={() => zoomAt(1.3, ...center())}><ZoomInIcon fontSize="small" /></IconButton>
        </span></Tooltip>
        <Tooltip title="Reset"><span>
          <IconButton size="small" disabled={!zoomed} onClick={reset}><ResetIcon fontSize="small" /></IconButton>
        </span></Tooltip>
      </Box>
    </Box>
  );
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Collapsible preview of a locally picked file (image or PDF) so the user can check the scan
 * against the fields extracted from it. `badge` renders at the right of the header (e.g. an
 * "Extracted" chip); `onRemove` adds a remove button.
 */
const LocalFilePreview = ({ file, defaultExpanded = false, badge = null, onRemove, disabled = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isImage = (file?.type || '').startsWith('image/');
  const isPdf = file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '');
  const [url, setUrl] = useState(null);

  // Created inside the effect so StrictMode's mount/unmount/mount cycle gets a fresh URL.
  useEffect(() => {
    if (!file || !(isImage || isPdf)) { setUrl(null); return undefined; }
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file, isImage, isPdf]);
  useEffect(() => { setExpanded(defaultExpanded); }, [file, defaultExpanded]);

  if (!file) return null;
  const canPreview = Boolean(url);
  const Icon = isImage ? ImageIcon : isPdf ? PdfIcon : FileIcon;

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', bgcolor: 'background.paper' }}>
      <Box
        onClick={() => canPreview && setExpanded((v) => !v)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5,
          cursor: canPreview ? 'pointer' : 'default',
          '&:hover': canPreview ? { bgcolor: 'action.hover' } : undefined
        }}
      >
        <Icon sx={{ fontSize: 18, color: 'secondary.main' }} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{file.name}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {formatSize(file.size)}{canPreview ? '' : ' · preview not available'}
          </Typography>
        </Box>
        {badge}
        {canPreview && (
          <Tooltip title={expanded ? 'Hide preview' : 'Show preview'}>
            <IconButton size="small" aria-label={expanded ? 'Hide preview' : 'Show preview'}>
              <ExpandMoreIcon
                fontSize="small"
                sx={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'none' }}
              />
            </IconButton>
          </Tooltip>
        )}
        {onRemove && (
          <Tooltip title="Remove">
            <span>
              <IconButton size="small" disabled={disabled} onClick={(e) => { e.stopPropagation(); onRemove(); }} aria-label="Remove file">
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {canPreview && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', p: 1 }}>
            {isImage ? (
              <ZoomableImage src={url} alt={file.name} />
            ) : (
              <Box component="iframe" title={file.name} src={url} sx={{ width: '100%', height: 420, border: 0, borderRadius: 1, bgcolor: '#fff' }} />
            )}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};

export default LocalFilePreview;
